<template>
  <div>
    <v-dialog
      v-model="dialog"
      max-width="700"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title>Test case</v-card-title>
        <v-card-text
          v-if="isNew"
          class="pt-2"
        >
          <SiInputText 
            v-model="searchCalculationId" 
            label="Calculation ID" 
            outlined 
            append-icon="search" 
            :error-messages="searchError" 
            autofocus 
            :loading="searchLoading" 
            @click:append="getTargetCalculation" 
            @keydown.enter="getTargetCalculation"
          />
        </v-card-text>
        <v-card-text
          v-else
          class="pt-2"
        >
          <SiInputText
            v-model="lazyTestCase.title"
            label="Title"
            outlined
          />
          <SiInputTextarea
            v-model="lazyTestCase.description"
            :rows="1"
            label="Description"
            outlined
          />
            
            
          <v-flex class="subtitle-1 mt-4">
            Test data
          </v-flex>
          <v-flex class="caption mb-1">
            Data that will be used to run calculation
          </v-flex>
          <v-divider />
          <v-treeview 
            :items="_treeViewObject({ data: lazyTestCase.data })"
            open-on-click
            dense
          >
            <template #label="{ item }">
              <div v-html="item.html" />
            </template>
          </v-treeview>
            
            
          <v-flex class="subtitle-1 mt-4">
            Tests
          </v-flex>
          <v-flex class="caption mb-1">
            Select the items you want to test from the target data below. Automate test will check it gets the same values.
          </v-flex>
          <v-divider />
          <v-data-table
            :headers="[{value: 'path'}, {value: 'target'}, {value: 'delete', align: 'end'},]"
            :items="_sortObject(lazyTestCase.tests)"
            hide-default-header
            hide-default-footer
          >
            <template #[`item.path`]="props">
              <v-edit-dialog :return-value.sync="props.item.path">
                {{ props.item.path }}
                <template #input>
                  <v-text-field
                    v-model="props.item.path"
                    label="Path"
                    single-line
                  />
                </template>
              </v-edit-dialog>
            </template>
            <template #[`item.target`]="props">
              <v-edit-dialog :return-value.sync="props.item.target">
                {{ props.item.target }}
                <template #input>
                  <v-text-field
                    v-model="props.item.target"
                    label="Target"
                    single-line
                  />
                </template>
              </v-edit-dialog>
            </template>
            <template #[`item.delete`]="props">
              <v-btn
                icon
                @click="deleteTest(props.item)"
              >
                <v-icon>close</v-icon>
              </v-btn>
            </template>
          </v-data-table>
          <v-flex class="subtitle-1 mt-4">
            Target data
          </v-flex>
          <v-treeview 
            :items="_treeViewObject(lazyTestCase.target_calculation)"
            open-on-click
            dense
          >
            <template #label="{ item }">
              <div
                :class="{'info--text': lazyTestCase.tests[safePath(item.path)]}"
                @click="toggleAsTest(item)"
                v-html="item.html"
              />
            </template>
          </v-treeview>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            color="grey"
            @click="dialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            v-if="!isNew"
            text
            color="primary"
            @click="save"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      color="success"
    >
      <div class="text-center">
        Test case successfully created
      </div>
    </v-snackbar>
  </div>
</template>

<script>
// import { mapGetters } from 'vuex'
import helpers from '@/mixins/helpers'
import { rtdb } from '@/plugins/firebase'
import { cloneDeep } from 'lodash'

  export default {
    mixins: [ helpers ],
    props: {
      testCase: {
        type: Object,
        default: () => ({})
      },
      showConfirm: Boolean,
      calculatorId: String,
      calculationId: String,
      value: Boolean,
    },
    data () {
      return {
        isNew: false,
        searchCalculationId: '',
        searchError: '',
        searchLoading: false,
        snackbar: false,
        lazyTestCase: {},
      }
    },
    computed: {
      dialog: {
        get () {
          return this.value
        },
        set (v) {
          this.$emit('input', v)
        }
      },
      testId () {
        return this.lazyTestCase.id
      }
    },
    watch: {
      dialog (v) {
        if (v) {
          if (!this.testCase.id) {
            this.isNew = true
            if (this.calculationId) {
              this.searchCalculationId = this.calculationId
              this.getTargetCalculation()
            }
          } else {
            this.lazyTestCase = { tests: {}, ...cloneDeep(this.testCase) }
          }
        }
      },
      searchCalculationId () {
        this.searchError = ''
      }
    },
    methods: {
      async getTargetCalculation () {
        this.searchError = ''
        this.searchLoading = true
        let id =  this.searchCalculationId
        let calc = await rtdb.ref(`calculations/${this.calculatorId}/${id}`).once('value').then(s => s.val())
        console.log(calc);
        if (calc) {
          let { calculationData:data, audit, report, result } = calc
          this.lazyTestCase = { id, data, target_calculation: { audit, report, result }, tests: {} }
          this.isNew = false 
          this.searchLoading = false
          this.searchCalculationId = ''
          console.log(this.lazyTestCase);
        } else {
          this.searchError = 'Unable to find calculation. Please check the id and try again'
        }
      },
      save () {
        rtdb.ref(`calculators/${this.calculatorId}/tests/${this.testId}`)
          .set(
            this._stringUndefined(this.lazyTestCase), 
            () => { if (this.showConfirm) this.snackbar = true}
          )
        this.lazyTestCase = {}
        this.dialog = false
      },
      toggleAsTest ({ path, value, children }) {
        if (children) return 
        let id = this.safePath(path)
        if (!this.lazyTestCase.tests[id]) {
          this.$set(this.lazyTestCase.tests, id, { path, target: value })
        } else {
          this.$delete(this.lazyTestCase.tests, id)
        }
      },
      deleteTest ({id}) {
        this.$delete(this.lazyTestCase.tests, id)
      },
      safePath (path) {
        return path.replace(/\./g, '+')
      }
    }
  }

</script>

<style lang="scss" scoped>

</style>
